import React, { Dispatch, SetStateAction, useCallback, useState } from 'react';

import { Button, Checkbox } from '@components/common';

import { IAnalysisItem, IAnalysisSchemaUrl } from '@interfaces/cmModule/schemaConfig';

import styles
	from '@components/routes/CmModule/DataSource/DataSourceGNBConfigurationTable/AttachMavenirConfig/CheckConfigModal/CheckConfigModal.module.scss';

interface IReplaceConfirmProps {
	setAnalyzedConfigs: Dispatch<SetStateAction<IAnalysisSchemaUrl[]>>;
	existingFiles: IAnalysisItem[];
	isSingleFile: boolean;
}

const ReplaceConfirm = ({
	setAnalyzedConfigs,
	existingFiles,
	isSingleFile,
}: IReplaceConfirmProps) => {
	const [isAllFiles, setIsAllFiles] = useState<boolean | undefined>();

	const currentFileName = existingFiles[0]?.fileName || null;
	const currentFileId = existingFiles[0]?.id || null;

	const groupOperation = useCallback((isReplace: boolean) => {
		setAnalyzedConfigs((prevState) => {
			return prevState.map((config) => {
				const isAllFilesAnalyzed = config.items.every((file) => file.isFileAnalyzed);

				if (isAllFilesAnalyzed) {
					return config;
				}

				return {
					...config,
					isReplaceAllDuplicates: isReplace,
					isSkipAllDuplicates: !isReplace,
					items: config.items.map((file) => ({
						...file,
						isFileAnalyzed: true,
					})),
				};
			});
		});
	}, [setAnalyzedConfigs]);

	const singleOperation = useCallback((isReplace: boolean) => {
		setAnalyzedConfigs((prevState) => {
			return prevState.map((config) => ({
				...config,
				items: config.items.map((file) => {
					if (file.id === currentFileId) {
						return {
							...file,
							isFileAnalyzed: true,
							isReplace: isReplace,
						};
					}
					return file;
				}),
			}));
		});
	}, [currentFileId, setAnalyzedConfigs]);

	const onChangeAllFiles = useCallback(() => {
		setIsAllFiles(prev => !prev);
	}, []);

	const onSkip = useCallback(() => {
		if (isAllFiles) {
			groupOperation(false);

			return;
		}

		singleOperation(false);
	}, [isAllFiles, singleOperation, groupOperation]);

	const onReplace = useCallback(() => {
		if (isAllFiles) {
			groupOperation(true);

			return;
		}
		singleOperation(true);
	}, [isAllFiles, singleOperation, groupOperation]);

	return (
		existingFiles.length
			? (
				<div className={styles.container}>
					<div className={styles.question}>
						Do you want to replace existing file or skip it while uploading?
					</div>
					<div className={styles.description}>The file {currentFileName} already exists.</div>
					<div className={styles.actions}>
						{!isSingleFile
							? (
								<Checkbox
									checked={isAllFiles}
									label='Use for all files'
									name='forAllFiles'
									onChange={onChangeAllFiles}
								/>
							)
							: <span />
						}
						<div className={styles.buttons}>
							<Button
								large
								onClick={onSkip}
								secondary
								text='Skip file'
							/>
							<Button
								large
								onClick={onReplace}
								text='Replace file'
							/>
						</div>
					</div>
				</div>
			)
			: null
	);
};

export default ReplaceConfirm;
